import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook to fetch notes, reposts, and quote notes for a specific pubkey
 */
export function useNotes(pubkey: string | undefined, enabled = true) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['notes', pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const events = await nostr.query([
        {
          kinds: [1, 6, 16], // Notes, reposts, and generic reposts
          authors: [pubkey],
          limit: 100,
        }
      ], { signal });

      // Sort by creation date descending
      return events.sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!pubkey && enabled,
    staleTime: 0, // Always consider data stale to refetch on tab switch
  });
}

/**
 * Validator for note events
 */
export function validateNoteEvent(event: NostrEvent): boolean {
  // Accept kind 1 (notes), kind 6 (reposts), and kind 16 (generic reposts)
  if (![1, 6, 16].includes(event.kind)) return false;

  // All these kinds should have content or appropriate tags
  return true;
}
